import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Card, Row, Col, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import AppDataGrid from "../../../components/common/AppDataGrid";
import { Tooltip } from '@mui/material';
import { MdEdit, MdDelete } from "react-icons/md";

function AddManageBudget() {
    const navigate = useNavigate();
    const routeLocation = useLocation();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};

    // Get edit data from navigation state
    const editData = routeLocation.state?.routeData || {};
    const isEditMode = editData.isEditMode || false;

    // Helper function to format date for input field
    function formatDateForInput(dateString) {
        if (!dateString) return "";

        // If date is in ISO format or similar
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; // Return as-is if not valid

        // Format as YYYY-MM-DD for input[type="date"]
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Initialize form with edit data or defaults - MATCHING .NET FIELDS
    const [form, setForm] = useState({
        budget_entry_id: editData.budget_entry_id || editData.hdnABudgetEntryId || null,
        project_id: editData.project_id || userObj.school_id || "",
        project_name: editData.project_name || userObj.school_name || "",
        budget_from_date: formatDateForInput(editData.budget_from_date) || "",
        budget_to_date: formatDateForInput(editData.budget_to_date) || "",
        account_code: "",
        amount: "",
        school_id: userObj?.school_id || ""
    });

    // Initialize budget list with edit data or empty
    const [budgetList, setBudgetList] = useState(editData.budgetList || []);
    const [budgetEntryId, setBudgetEntryId] = useState(editData.budget_entry_id || editData.hdnABudgetEntryId || null);
    const [editDetailId, setEditDetailId] = useState(null);
    const [isEditingRow, setIsEditingRow] = useState(false);
    const [datesLocked, setDatesLocked] = useState(isEditMode || editData.datesLocked || false);
    const [groupCodes, setGroupCodes] = useState([]);

    useEffect(() => {
        GetGroupaccount();

        // If in edit mode, lock dates and disable project selection
        if (isEditMode) {
            setDatesLocked(true);
        }

        // Log for debugging
        console.log("Edit Data received:", editData);
        console.log("Form initialized:", form);
        console.log("Budget List initialized:", budgetList);
    }, []);

    const GetGroupaccount = async () => {
        try {
            const response = await axios.get(baseUrl + "/getgroupaccount/");
            const accounts = response.data.group_accounts || [];
            const formatted = accounts.map((acc) => ({
                value: acc.account_master_id,
                label: acc.name_on_account
            }));
            setGroupCodes(formatted);

            // If we're in edit mode and have groupCodes, update budgetList with proper labels
            if (isEditMode && budgetList.length > 0 && formatted.length > 0) {
                const updatedList = budgetList.map(item => {
                    const account = formatted.find(acc => acc.value === item.account_master_id);
                    return {
                        ...item,
                        ac_id: item.account_master_id,
                        ac_code: account ? account.label : item.group_accountname || "Unknown"
                    };
                });
                setBudgetList(updatedList);
            }
        } catch (error) {
            console.log("Error fetching accounts:", error);
        }
    };

   const checkDuplicateBudget = async (fromDate, toDate) => {
    // Do not check in edit mode (same as before)
    if (isEditMode) return;

    // Mandatory validations
    if (!fromDate || !toDate || !form.project_id) return;

    try {
        const payload = {
            budget_from_date: fromDate,
            budget_to_date: toDate,
            account_master_id: form.project_id,          
            budget_entry_id: form.budget_entry_id || 0
        };

        const res = await axios.post(
            `${baseUrl}/check-duplication-budget-entry/`,
            payload,
            { headers: { "Content-Type": "application/json" } }
        );

        const rawData = res.data;
        let isDuplicate = false;

        if (typeof rawData === "number") {
            isDuplicate = rawData === 1;
        } else if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
            const flag = rawData.duplicate_flag ?? rawData.is_duplicate;
            isDuplicate = flag === 1 || flag === true;
        } else if (Array.isArray(rawData)) {
            isDuplicate = rawData.length > 0;
        }

        if (!isDuplicate) return;

        try {
            const filterPayload = {
                budget_entry_id: 0,
                budget_from_date: fromDate,
                budget_to_date: toDate,
                school_id: form.project_id || 0
            };

            const filterRes = await axios.post(
                `${baseUrl}/budgetentryfilter/`,
                filterPayload,
                { headers: { "Content-Type": "application/json" } }
            );

            const rows = Array.isArray(filterRes.data) ? filterRes.data : [];

            const hasActive = rows.some((row) => {
                const statusText = (row.status || row.budget_status || "")
                    .toString()
                    .toLowerCase();

                const isActiveFlag =
                    row.is_active === "Y" ||
                    row.is_active === true ||
                    row.status_id === 1;

                return statusText === "active" || isActiveFlag;
            });

            if (!hasActive) {
                return;
            }
        } catch (innerErr) {
            console.error("Error while checking active/inactive duplicates:", innerErr);
        }

        toast.warning(
            "Record Already Exists Between the Entered From Date and To Date"
        );

        setForm((prev) => ({
            ...prev,
            budget_from_date: "",
            budget_to_date: ""
        }));
    } catch (err) {
        console.error("Duplicate check failed:", err);
       
    }
};


    const handleInputChange = (e) => {
        const { id, value } = e.target;

        setForm(prev => {
            const updated = { ...prev, [id]: value };

            // Trigger duplicate check ONLY when both dates exist
            if (
                (id === "budget_from_date" || id === "budget_to_date") &&
                updated.budget_from_date &&
                updated.budget_to_date
            ) {
                checkDuplicateBudget(
                    updated.budget_from_date,
                    updated.budget_to_date
                );
            }

            return updated;
        });
    };

    const handleSaveBudget = async () => {
        if (!form.budget_from_date || !form.budget_to_date) {
            toast.error("Please select From and To dates");
            return;
        }

        if (budgetList.length === 0) {
            toast.error("Add at least one A/C Code");
            return;
        }

        try {
            const payload = {
                budget_entry_id: isEditMode ? form.budget_entry_id : 0,
                budget_from_date: form.budget_from_date,
                budget_to_date: form.budget_to_date,
                project_id: form.project_id,
                created_by: userObj.username,
                action: isEditMode ? "UPDATE" : "CREATE"
            };

            console.log("Sending to addupdatebudget_entry:", payload);

            const response = await axios.post(
                `${baseUrl}/addupdatebudget_entry/`,  
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                const budget_entry_id = response.data.budget_entry_id;

                if (budget_entry_id) {
                    await saveBudgetDetails(budget_entry_id);

                    toast.success(isEditMode ? "Budget updated successfully!" : "Budget saved successfully!");
                } else {
                    toast.error("Failed to get budget entry ID from server");
                }
            } else {
                toast.error("Failed to save budget");
            }

        } catch (error) {
            console.error("Error saving budget:", error);
            if (error.response) {
                console.error("Response error:", error.response.data);
                toast.error(`Error: ${error.response.data.error || error.response.statusText}`);
            } else if (error.request) {
                console.error("Request error:", error.request);
                toast.error("Network error - please check your connection");
            } else {
                toast.error("Error saving budget");
            }
        }
    };

    const saveBudgetDetails = async (budget_entry_id) => {
        if (!budget_entry_id) {
            throw new Error("No budget_entry_id provided");
        }

        // Save each detail record
        const detailPromises = budgetList.map(async (item) => {
            const detailPayload = {
                budget_entry_detail_id: item.budget_entry_detail_id || 0,
                budget_entry_id: budget_entry_id,
                account_master_id: item.ac_id || item.account_master_id,
                amount: parseFloat(item.amount) || 0,
                created_by: userObj.username,
                action: item.budget_entry_detail_id ? "UPDATE" : "CREATE"
            };

            console.log("Saving detail:", detailPayload);

            return axios.post(
                `${baseUrl}/addupdatebudget_entry_detail/`,
                detailPayload,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
        });


        await Promise.all(detailPromises);
        console.log("All budget details saved successfully");
    };

    const isDuplicateAccountCode = (accountCode) => {
        return budgetList.some((item) => {
            // ADD mode
            if (!isEditingRow) {
                return item.ac_id === accountCode;
            }

            // EDIT mode (ignore current editing row)
            return (
                item.ac_id === accountCode &&
                item.id !== editDetailId
            );
        });
    };

    // 🔹 UPDATED RESET LOGIC (keeps already added records in edit mode)
    const handleReset = () => {
        // If user is editing a single row, just cancel row edit & clear inputs
        if (isEditingRow) {
            setIsEditingRow(false);
            setEditDetailId(null);
            setForm(prev => ({
                ...prev,
                account_code: "",
                amount: ""
            }));
            return;
        }

        if (isEditMode) {
            // In edit mode: DO NOT reset already added records (budgetList)
            // Just clear the current input fields
            setForm(prev => ({
                ...prev,
                account_code: "",
                amount: ""
            }));
            return;
        }

        // In add mode: keep your original full reset behavior
        setForm({
            budget_entry_id: null,
            project_id: userObj.school_id || "",
            project_name: userObj.school_name || "",
            budget_from_date: "",
            budget_to_date: "",
            account_code: "",
            amount: "",
            school_id: userObj?.school_id || "",
        });

        setBudgetList([]);
        setDatesLocked(false);
        setIsEditingRow(false);
        setEditDetailId(null);
    };

    const handleAddBudget = (e) => {
        e.preventDefault();

        if (!form.account_code || !form.amount) {
            toast.error("Please select A/C Code and enter Amount");
            return;
        }

        if (isDuplicateAccountCode(form.account_code)) {
            toast.warning("Account Code Already exists");
            return;
        }

        if (isEditingRow) {
            // UPDATE EXISTING ROW
            const selectedAccount = groupCodes.find(acc => acc.value === form.account_code);

            setBudgetList((prev) =>
                prev.map((row) =>
                    row.id === editDetailId
                        ? {
                            ...row,
                            ac_id: form.account_code,
                            ac_code: selectedAccount ? selectedAccount.label : "Unknown",
                            amount: form.amount,
                        }
                        : row
                )
            );

            setIsEditingRow(false);
            setEditDetailId(null);
            toast.success("Updated successfully");
        } else {
            // ADD NEW ROW
            const selectedAccount = groupCodes.find(acc => acc.value === form.account_code);

            if (!selectedAccount) {
                toast.error("Selected account not found");
                return;
            }

            const newItem = {
                id: Date.now(),
                budget_entry_detail_id: null,
                ac_id: selectedAccount.value,
                ac_code: selectedAccount.label,
                amount: form.amount,
                from_date: form.budget_from_date,
                to_date: form.budget_to_date
            };

            setBudgetList(prev => [...prev, newItem]);

            // Lock dates after first entry if not already locked
            if (!datesLocked) {
                setDatesLocked(true);
            }

            toast.success("Added successfully");
        }

        // Reset fields after add/update
        setForm(f => ({
            ...f,
            account_code: "",
            amount: ""
        }));
    };

    // const handleDeleteRow = (id, e) => {
    //     e?.stopPropagation();
    //     if (!window.confirm("Are you sure you want to remove this account?")) return;

    //     setBudgetList(prev => prev.filter(row => row.id !== id));

    //     // If no rows left, unlock dates (only if not in edit mode)
    //     if (budgetList.length === 1 && !isEditMode) {
    //         setDatesLocked(false);
    //     }

    //     toast.success("Account removed");
    // };

    // Grid columns with edit and delete actions
    const gridColumns = [
        { field: "ac_code", headerName: "Account Code", flex: 1 },
        { field: "amount", headerName: "Amount", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => (
                <div className="tableActions">
                    <Tooltip title="Edit" arrow>
                        <span
                            className="commonActionIcons"
                            style={{ cursor: "pointer", color: "#1976d2", marginRight: "10px" }}
                            onClick={() => {
                                const selected = budgetList.find((r) => r.id === params.row.id);
                                if (selected) {
                                    setIsEditingRow(true);
                                    setEditDetailId(selected.id);
                                    setForm((prev) => ({
                                        ...prev,
                                        account_code: selected.ac_id,
                                        amount: selected.amount,
                                    }));
                                }
                            }}
                        >
                            <MdEdit size={18} />
                        </span>
                    </Tooltip>
                    {/* <Tooltip title="Delete" arrow>
                        <span
                            className="commonActionIcons"
                            style={{ cursor: "pointer", color: "#dc3545" }}
                            onClick={(e) => handleDeleteRow(params.row.id, e)}
                        >
                            <MdDelete size={18} />
                        </span>
                    </Tooltip> */}
                </div>
            )
        }
    ];

    return (
        <div className="pageBody">
            <ToastContainer />
            <Container fluid>
                <Card>
                    <Card.Body>
                        <form>
                            <div>
                                <Row>
                                    <Col xs={12}>
                                        <h6 className="commonSectionTitle">
                                            {isEditMode ? "Edit Manage Budget" : "Add Manage Budget"}
                                        </h6>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>
                                                    School Name <span className="requiredStar">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={form.project_name}
                                                    disabled
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>From Date<span className="requiredStar">*</span></Form.Label>
                                                <Form.Control
                                                    required
                                                    type="date"
                                                    id="budget_from_date"
                                                    value={form.budget_from_date}
                                                    maxLength={50}
                                                    max={form.budget_to_date || undefined}
                                                    onChange={handleInputChange}
                                                    disabled={datesLocked}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>To Date<span className="requiredStar">*</span></Form.Label>
                                                <Form.Control
                                                    required
                                                    type="date"
                                                    id="budget_to_date"
                                                    value={form.budget_to_date}
                                                    min={form.budget_from_date || undefined}
                                                    maxLength={50}
                                                    onChange={handleInputChange}
                                                    disabled={datesLocked}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>A/C Code <span className="requiredStar">*</span></Form.Label>
                                                <Select
                                                    inputId="account_code"
                                                    classNamePrefix="react-select"
                                                    placeholder="--Select Account Code--"
                                                    isSearchable
                                                    isClearable
                                                    value={groupCodes.find((o) => o.value === form.account_code) || null}
                                                    onChange={(opt) =>
                                                        setForm((f) => ({
                                                            ...f,
                                                            account_code: opt ? opt.value : "",
                                                        }))
                                                    }
                                                    options={groupCodes}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <div className="commonInput">
                                            <Form.Group>
                                                <Form.Label>
                                                    Amount<span className="requiredStar">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    required
                                                    type="number"
                                                    id="amount"
                                                    value={form.amount}
                                                    placeholder="Enter Amount"
                                                    min="0"
                                                    maxLength={50}
                                                    onChange={handleInputChange}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "-" || e.key === "e") {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6} lg={4} xxl={3}>
                                        <Button
                                            style={{
                                                height: "fit-content",
                                                whiteSpace: "nowrap",
                                                marginBottom: "4%",
                                                marginLeft: "5%",
                                                marginTop: "8%"
                                            }}
                                            type="button"
                                            variant="primary"
                                            className="btn-success primaryBtn"
                                            onClick={handleAddBudget}
                                        >
                                            {isEditingRow ? "Update" : "+ Add"}
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Budget Grid Section */}
                                {budgetList.length > 0 && (
                                    <Row className="mt-4">
                                        <Col xs={12}>
                                            <AppDataGrid
                                                rows={budgetList}
                                                columns={gridColumns}
                                                getRowId={(r) => r.id}
                                                showToolbar={false}
                                                pagination
                                                pageSizeOptions={[5, 10]}
                                                density="compact"
                                                maxHeight={400}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </div>

                            <div className="d-flex justify-content-between mt-3">
                                <Button
                                    id="resetBudgetBtn"
                                    type="button"
                                    variant="primary"
                                    className="btn-info clearBtn"
                                    onClick={handleReset}
                                >
                                    Reset
                                </Button>

                                <div>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        className="btn-danger secondaryBtn me-2"
                                        onClick={() => navigate("/finance/budget")}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        className="btn-success primaryBtn"
                                        onClick={handleSaveBudget}
                                    >
                                        {isEditMode ? "Update" : "Submit"}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default AddManageBudget;
